import { DatePipe } from '@angular/common';
import { Component, Input, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { UI, type UiBadgeVariant, type UiTab } from '../../components/ui';
import type { EntityConfig } from '../../config/entity-config';
import {
  MailingService,
  choiceValue,
  type CampaignCategory,
  type CampaignHistoryItem,
  type CampaignSchedule,
} from '../../services/mailing.service';
import { RoleAccessService } from '../../services/role-access.service';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { apiErrorMessage } from '../campaign-create/campaign-create.component';
import { MailingComposerComponent } from './mailing-composer.component';

type ScheduleAction = 'pause' | 'resume' | 'run';

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

/** Back-office Mailings: monthly statistics schedule, marketing, announcements and the welcome rollout. */
@Component({
  selector: 'app-mailings-page',
  standalone: true,
  imports: [DatePipe, FormsModule, RouterLink, MailingComposerComponent, ...UI],
  template: `
    <section class="mx-auto w-full max-w-5xl px-4 py-3">
      <h1 class="m-0 mb-1 font-heading text-lg font-semibold text-fg">Mailings</h1>
      <p class="mb-3 text-sm text-muted">
        Email campaigns sent by the platform: the monthly statistics, marketing messages, provider
        and market announcements and the welcome rollout.
      </p>

      <ui-tabs [tabs]="tabItems" [active]="tab()" (activeChange)="tab.set($event)" />

      <div class="mt-4">
        @switch (tab()) {
          @case ('stats') {
            @if (message()) {
              <ui-alert variant="info" class="mb-3 block">{{ message() }}</ui-alert>
            }
            @if (error()) {
              <ui-alert variant="danger" class="mb-3 block">{{ error() }}</ui-alert>
            }
            @if (loading()) {
              <ui-spinner />
            }

            @for (s of schedules(); track s.id) {
              <ui-card [title]="s.name" class="mb-3 block">
                <div header-actions class="flex flex-wrap gap-2">
                  @if (canAct()) {
                    @if (s.is_active) {
                      <button
                        type="button"
                        uiButton
                        variant="outline"
                        size="sm"
                        [disabled]="busy()"
                        (click)="act(s, 'pause')"
                      >
                        Pause
                      </button>
                    } @else {
                      <button
                        type="button"
                        uiButton
                        variant="outline"
                        size="sm"
                        [disabled]="busy()"
                        (click)="act(s, 'resume')"
                      >
                        Resume
                      </button>
                    }
                    <button
                      type="button"
                      uiButton
                      variant="outline"
                      size="sm"
                      [disabled]="busy()"
                      (click)="openReschedule(s)"
                    >
                      Reschedule
                    </button>
                    <button
                      type="button"
                      uiButton
                      size="sm"
                      [disabled]="busy()"
                      (click)="confirmRun.set(s)"
                    >
                      Run now
                    </button>
                  }
                </div>
                <dl class="m-0 grid grid-cols-1 gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
                  <div>
                    <dt class="text-xs text-muted">Status</dt>
                    <dd class="m-0">
                      <ui-badge [variant]="s.is_active ? 'success' : 'neutral'">{{
                        s.is_active ? 'Active' : 'Paused'
                      }}</ui-badge>
                    </dd>
                  </div>
                  <div>
                    <dt class="text-xs text-muted">Category</dt>
                    <dd class="m-0">{{ s.category_name }}</dd>
                  </div>
                  <div>
                    <dt class="text-xs text-muted">Runs</dt>
                    <dd class="m-0">
                      {{ dayLabel(s.day_of_month) }} at {{ pad(s.run_hour_utc) }}:00 UTC
                    </dd>
                  </div>
                  <div>
                    <dt class="text-xs text-muted">Next run</dt>
                    <dd class="m-0">
                      {{
                        s.is_active && s.next_run_at
                          ? (s.next_run_at | date: 'medium')
                          : 'None scheduled'
                      }}
                    </dd>
                  </div>
                  <div>
                    <dt class="text-xs text-muted">Last run</dt>
                    <dd class="m-0">
                      @if (s.last_run_at) {
                        {{ s.last_run_at | date: 'medium' }}
                        @if (s.last_status) {
                          <ui-badge class="ml-1" [variant]="statusVariant(s.last_status)">{{
                            s.last_status
                          }}</ui-badge>
                        }
                      } @else {
                        Never
                      }
                    </dd>
                  </div>
                  @if (s.last_message) {
                    <div>
                      <dt class="text-xs text-muted">Last message</dt>
                      <dd class="m-0 text-muted">{{ s.last_message }}</dd>
                    </div>
                  }
                </dl>
              </ui-card>
            } @empty {
              @if (!loading()) {
                <ui-card title="Monthly statistics" class="mb-3 block">
                  <p class="mt-0 text-sm text-muted">
                    No schedule yet. Create one to email every merchant its monthly statistics.
                  </p>
                  @if (canAct()) {
                    <div class="grid grid-cols-1 gap-x-4 md:grid-cols-2">
                      <ui-form-field label="Name">
                        <input uiInput type="text" [(ngModel)]="newName" name="newName" />
                      </ui-form-field>
                      <ui-form-field label="Category">
                        <select uiInput [(ngModel)]="newCategory" name="newCategory">
                          @for (c of statsCategories(); track c.id) {
                            <option [value]="c.id">{{ c.name }}</option>
                          } @empty {
                            <option value="">No active monthly statistics category</option>
                          }
                        </select>
                      </ui-form-field>
                      <ui-form-field label="Day of month">
                        <select uiInput [(ngModel)]="newDay" name="newDay">
                          @for (d of days; track d) {
                            <option [ngValue]="d">{{ d }}</option>
                          }
                          <option [ngValue]="31">Last day</option>
                        </select>
                      </ui-form-field>
                      <ui-form-field label="Hour (UTC)">
                        <select uiInput [(ngModel)]="newHour" name="newHour">
                          @for (h of hours; track h) {
                            <option [ngValue]="h">{{ pad(h) }}:00</option>
                          }
                        </select>
                      </ui-form-field>
                    </div>
                    <button
                      type="button"
                      uiButton
                      [disabled]="busy() || !newCategory || !newName.trim()"
                      (click)="createSchedule()"
                    >
                      Create schedule
                    </button>
                  }
                </ui-card>
              }
            }

            @if (schedules().length) {
              <ui-card title="History" class="block">
                <div uiTableWrap>
                  <table uiTable>
                    <thead>
                      <tr>
                        <th>Campaign</th>
                        <th>Created</th>
                        <th>Status</th>
                        <th>Recipients</th>
                        <th>Sent</th>
                        <th>Failed</th>
                      </tr>
                    </thead>
                    <tbody>
                      @for (h of history(); track h.id) {
                        <tr>
                          <td>
                            <a
                              class="text-brand hover:underline"
                              [routerLink]="shellRoutes.commands('manage', 'campaigns', h.id)"
                            >
                              {{ h.name || h.reference }}
                            </a>
                          </td>
                          <td>{{ h.created_at | date: 'medium' }}</td>
                          <td>
                            <ui-badge [variant]="statusVariant(status(h))">{{
                              status(h)
                            }}</ui-badge>
                          </td>
                          <td>{{ h.total_count ?? 0 }}</td>
                          <td>{{ h.sent_count ?? 0 }}</td>
                          <td>{{ h.failed_count ?? 0 }}</td>
                        </tr>
                      } @empty {
                        <tr>
                          <td colspan="6" class="text-muted">No campaign yet.</td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </ui-card>
            }
          }
          @case ('marketing') {
            <ui-card title="Marketing email" class="block">
              <app-mailing-composer mode="marketing" />
            </ui-card>
          }
          @case ('announcements') {
            <ui-card title="Provider or market announcement" class="block">
              <app-mailing-composer mode="announcement" />
            </ui-card>
          }
          @case ('welcome') {
            <ui-card title="Welcome rollout" class="block">
              <app-mailing-composer mode="welcome" />
            </ui-card>
          }
        }
      </div>
    </section>

    <ui-modal
      [open]="!!rescheduling()"
      title="Reschedule"
      size="sm"
      (closed)="rescheduling.set(null)"
    >
      <ui-form-field label="Day of month">
        <select uiInput [(ngModel)]="editDay" name="editDay">
          @for (d of days; track d) {
            <option [ngValue]="d">{{ d }}</option>
          }
          <option [ngValue]="31">Last day</option>
        </select>
      </ui-form-field>
      <ui-form-field label="Hour (UTC)">
        <select uiInput [(ngModel)]="editHour" name="editHour">
          @for (h of hours; track h) {
            <option [ngValue]="h">{{ pad(h) }}:00</option>
          }
        </select>
      </ui-form-field>
      <ng-container modal-footer>
        <button type="button" uiButton variant="outline" (click)="rescheduling.set(null)">
          Cancel
        </button>
        <button type="button" uiButton [disabled]="busy()" (click)="saveReschedule()">Save</button>
      </ng-container>
    </ui-modal>

    <ui-confirm-dialog
      [open]="!!confirmRun()"
      title="Run now?"
      message="This sends the emails immediately, in addition to the regular schedule."
      confirmLabel="Run now"
      (confirmed)="runNow()"
      (cancelled)="confirmRun.set(null)"
    />
  `,
})
export class MailingsPageComponent implements OnInit {
  @Input() entityConfig?: EntityConfig;

  private readonly svc = inject(MailingService);
  private readonly roleAccess = inject(RoleAccessService);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly days = DAYS;
  readonly hours = HOURS;
  readonly tabItems: UiTab[] = [
    { id: 'stats', label: 'Monthly statistics' },
    { id: 'marketing', label: 'Marketing' },
    { id: 'announcements', label: 'Announcements' },
    { id: 'welcome', label: 'Welcome rollout' },
  ];

  readonly tab = signal('stats');
  readonly schedules = signal<CampaignSchedule[]>([]);
  readonly history = signal<CampaignHistoryItem[]>([]);
  readonly statsCategories = signal<CampaignCategory[]>([]);
  readonly loading = signal(true);
  readonly busy = signal(false);
  readonly message = signal<string | null>(null);
  readonly error = signal<string | null>(null);
  readonly rescheduling = signal<CampaignSchedule | null>(null);
  readonly confirmRun = signal<CampaignSchedule | null>(null);
  readonly canAct = computed(() => this.roleAccess.isActiveSystemRole());

  newName = 'Monthly statistics';
  newCategory = '';
  newDay = 1;
  newHour = 7;
  editDay = 1;
  editHour = 7;

  ngOnInit(): void {
    this.reload();
    this.svc.categories('MONTHLY_STATS').subscribe({
      next: (page) => {
        const list = page.results.filter((c) => choiceValue(c.kind) === 'MONTHLY_STATS');
        this.statsCategories.set(list);
        this.newCategory = list[0]?.id ?? '';
      },
      error: () => this.statsCategories.set([]),
    });
  }

  pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  dayLabel(day: number): string {
    return day === 31 ? 'Last day of the month' : `Day ${day} of the month`;
  }

  status(h: CampaignHistoryItem): string {
    return choiceValue(h.status);
  }

  statusVariant(status: string): UiBadgeVariant {
    const s = status.toUpperCase();
    if (s === 'COMPLETED' || s === 'OK' || s === 'SUCCESS') return 'success';
    if (s.includes('FAIL') || s === 'ERROR') return 'danger';
    if (s === 'PROCESSING' || s === 'PARTIAL' || s === 'PAUSED') return 'warning';
    if (s === 'READY') return 'info';
    return 'neutral';
  }

  act(schedule: CampaignSchedule, action: ScheduleAction): void {
    const call =
      action === 'pause'
        ? this.svc.pauseSchedule(schedule.id)
        : action === 'resume'
          ? this.svc.resumeSchedule(schedule.id)
          : this.svc.runScheduleNow(schedule.id);
    this.run(
      call,
      action === 'run'
        ? 'Run started.'
        : action === 'pause'
          ? 'Schedule paused.'
          : 'Schedule resumed.',
    );
  }

  runNow(): void {
    const schedule = this.confirmRun();
    this.confirmRun.set(null);
    if (schedule) this.act(schedule, 'run');
  }

  openReschedule(schedule: CampaignSchedule): void {
    this.editDay = schedule.day_of_month;
    this.editHour = schedule.run_hour_utc;
    this.rescheduling.set(schedule);
  }

  saveReschedule(): void {
    const schedule = this.rescheduling();
    if (!schedule) return;
    this.rescheduling.set(null);
    this.run(
      this.svc.rescheduleSchedule(schedule.id, {
        day_of_month: this.editDay,
        run_hour_utc: this.editHour,
      }),
      'Schedule updated.',
    );
  }

  createSchedule(): void {
    this.run(
      this.svc.createSchedule({
        name: this.newName.trim(),
        category: this.newCategory,
        day_of_month: this.newDay,
        run_hour_utc: this.newHour,
      }),
      'Schedule created.',
    );
  }

  private run(call: ReturnType<MailingService['pauseSchedule']>, text: string): void {
    this.busy.set(true);
    this.error.set(null);
    this.message.set(null);
    call.subscribe({
      next: () => {
        this.busy.set(false);
        this.message.set(text);
        this.reload();
      },
      error: (err) => {
        this.busy.set(false);
        this.error.set(apiErrorMessage(err, 'The action failed.'));
      },
    });
  }

  private reload(): void {
    this.svc.schedules().subscribe({
      next: (page) => {
        this.schedules.set(page.results);
        this.loading.set(false);
        const first = page.results[0];
        if (first) {
          this.svc.scheduleHistory(first.id).subscribe({
            next: (rows) => this.history.set(rows),
            error: () => this.history.set([]),
          });
        } else {
          this.history.set([]);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Could not load the schedules.');
      },
    });
  }
}
